import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { queryKeys } from '@/api/query-keys'
import { toWireTextChains } from '@/lib/text-linking/text-chain-mutator'
import { useElementsLock } from '@/hooks/use-elements-lock'
import { useChangeQueue } from '@/hooks/use-change-queue'
import { useGroupActionQueue } from '@/hooks/use-group-action-queue'
import { groupGeometricPersistOpFromTransformSource } from '@/api/services/group-actions.service'
import {
  buildStowedVisibleRectOptions,
  isStowedTextElement,
} from '@/lib/elements/stowed-element-builder'
import { checkpointsService } from '@/api/services/checkpoints.service'
import { elementsService } from '@/api/services/elements.service'
import { buildGroupTransformPersistBatch } from '@/lib/groups/build-group-transform-persist-batch'
import { isDescendantOfGroup } from '@/lib/groups/shape-group-tree'
import { getStoredJwtToken } from '@/api/services/auth.service'
import { getStoredConnectionId } from '@/providers/websocket'
import { logGroupRotateDebug } from '@/lib/debug/shape-geometry-debug'
import { logger } from '@/utils/logger'
import { useSpreadScopedUndoRedo } from '../use-spread-scoped-undo-redo'
import { settleFailedBackgroundRestore } from '../../handlers/settle-failed-background-restore'
import type { HistoryPersistencePlan } from '@/lib/history/types/history-persistence-plan'
import type { UndoAction } from '@/lib/history/types/undo-action'
import type {
  RestoreSpreadBackgrounds,
  SpreadBackgroundsChange,
} from '@/lib/backgrounds/spread-backgrounds-history'
import { getApiBaseUrl } from '@/config/config'
import { finalizeActiveShapeTextEdit } from '@/lib/shapes/shape-text-edit-session'
import type {
  HomeDocumentLayout,
  HomeFoundation,
  HomeToolsAndSelection,
  HomeZoomAndSpread,
} from '../../types/home-groups'
import { buildStowedShapeElement } from '../../helpers/build-stowed-shape-element'
import { buildGroupRepositionPayload } from '../../helpers/build-group-reposition-payload'
import { mixedGeometricOpFromHistoryTxnId } from '../../helpers/mixed-geometric-op'

/**
 * Persistence plumbing: undo/redo and the group-transaction predicates, the active-shape selection
 * mirror, checkpoint-derived editor settings, the change / group-action queues and element locks,
 * the spread switch and page-exit lifecycle, and applyHistoryPersistencePlan, which re-persists an
 * undo/redo step.
 */
export function useHomePersistence(
  deps: Pick<
    HomeFoundation,
    | 'setTextChains'
    | 'setUnifiedLayerIds'
    | 'setSelectedTextBoxPanelIds'
    | 'queryClient'
    | 'shapeManagerRef'
    | 'shapeTextBakeRef'
    | 'centerCanvasRef'
    | 'fontLookupRef'
    | 'textChainsRef'
    | 'chainSeamMemoRef'
    | 'chainProjectionBaselineRef'
    | 'unifiedLayerIdsRef'
  > &
    Pick<HomeZoomAndSpread, 'data' | 'setSpreadIndex' | 'spreadId' | 'spreadIdRef'> &
    Pick<HomeDocumentLayout, 'spreadProjection'> &
    Pick<
      HomeToolsAndSelection,
      | 'setActiveObject'
      | 'setActiveObjects'
      | 'setActiveTextBoxes'
      | 'setActiveObjectId'
      | 'setHasTextSelection'
    >
) {
  const {
    setTextChains,
    setUnifiedLayerIds,
    setSelectedTextBoxPanelIds,
    queryClient,
    shapeManagerRef,
    shapeTextBakeRef,
    centerCanvasRef,
    fontLookupRef,
    textChainsRef,
    chainSeamMemoRef,
    chainProjectionBaselineRef,
    unifiedLayerIdsRef,
    data,
    setSpreadIndex,
    spreadId,
    spreadIdRef,
    spreadProjection,
    setActiveObject,
    setActiveObjects,
    setActiveTextBoxes,
    setActiveObjectId,
    setHasTextSelection,
  } = deps

  // Undo/redo — scoped to the spread on screen (another spread's background steps are skipped).
  const {
    canUndo,
    canRedo,
    pushAction,
    undo,
    redo,
    getUndoStackSize,
    popIfMatches,
    revertHistoryStepIfMatches,
    dropReplayedStepIfMatches,
  } = useSpreadScopedUndoRedo(spreadId, spreadIdRef)
  const pendingUngroupChildrenByTxnRef = useRef<Map<string, Map<string, Set<string>>>>(new Map())

  // Spread backgrounds live in `BackgroundPlacementProvider`, which renders below home. It reports
  // each committed edit through `recordSpreadBackgroundsChange` and assigns this bridge so a
  // history step can restore (and save) a snapshot.
  const restoreSpreadBackgroundsRef = useRef<RestoreSpreadBackgrounds | null>(null)
  // Also assigned by the provider: waits for a spread's queued background writes. Background saves
  // don't go through ChangeQueue, so leaving a spread waits on this too before exiting its
  // checkpoint — a late write would re-register this session on the exited spread.
  const flushSpreadBackgroundWritesRef = useRef<((spreadId: string) => Promise<void>) | null>(null)

  /** Records a background edit and returns its discard: if the edit's save fails and rolls back,
   *  the entry is dropped again — but only while it's still the top of the stack, so a later edit
   *  is never disturbed (a left-over entry would just undo to the state already showing). */
  const recordSpreadBackgroundsChange = useCallback(
    ({ spreadId: changedSpreadId, before, after }: SpreadBackgroundsChange) => {
      const action: UndoAction = {
        type: 'spreadBackgrounds',
        spreadId: changedSpreadId,
        before,
        after,
      }

      pushAction(action)
      return () => {
        popIfMatches((top) => top === action)
      }
    },
    [popIfMatches, pushAction]
  )

  const isGroupShape = useCallback((shape: ShapeData) => shape.kindId === 7, [])

  const isGroupTxn = useCallback(
    (historyTxnId?: string) => !!historyTxnId?.startsWith('group:'),
    []
  )
  const isUngroupTxn = useCallback(
    (historyTxnId?: string) => !!historyTxnId?.startsWith('ungroup:'),
    []
  )

  const isParentGroupOnlyUndo = useCallback((undo?: ShapeCommittedUndoMeta): boolean => {
    if (!undo) return false
    if (undo.before.parentGroupId === undo.after.parentGroupId) return false

    const touchesGeometry =
      undo.before.x !== undo.after.x ||
      undo.before.y !== undo.after.y ||
      undo.before.width !== undo.after.width ||
      undo.before.height !== undo.after.height ||
      undo.before.rotation !== undo.after.rotation
    const touchesPath = undo.before.path !== undo.after.path
    const touchesPathCommands = undo.before.pathCommands !== undo.after.pathCommands

    return !touchesGeometry && !touchesPath && !touchesPathCommands
  }, [])

  /** Effects panel transparency (WMPCM): after a committed value, clear shape + text selection (no undo stack entry). */
  const handleTransparencyCommitApplied = useCallback(() => {
    shapeManagerRef.current?.clearSelection({ recordHistory: false })
  }, [shapeManagerRef])

  /** Push a resolved shape selection into the panel mirror (`activeObjects` / `activeObject`). */
  const applyActiveShapeSelection = useCallback(
    (selected: ShapeData[]) => {
      setActiveObjects(selected)
      if (selected.length === 1) {
        setActiveObject(selected[0])
        setActiveObjectId(selected[0].id)
      } else if (selected.length === 0) {
        setActiveObject(null)
        setActiveObjectId(null)
      } else {
        // Multi-select: do not call setActiveObject(null) — it replaces activeObjects with [].
        // activeObject is already derived as activeObjects[0] in context.
        setActiveObjectId(null)
      }
    },
    [setActiveObjects, setActiveObject, setActiveObjectId]
  )

  const syncActiveFromShapeSelection = useCallback(() => {
    const mgr = shapeManagerRef.current

    if (!mgr) return
    const shapes = mgr.getShapes()
    const selectedIds = mgr.getSelectedShapeIds()
    const selected = selectedIds
      .map((id) => shapes.find((s) => s.id === id))
      .filter((s): s is ShapeData => s != null)

    applyActiveShapeSelection(selected)
  }, [applyActiveShapeSelection, shapeManagerRef])

  const checkpointEditorSettings = useMemo(() => {
    // NsDocument from the API is the sole source of checkpoint document structure (see extractPageTopics).
    const asRecord = (v: unknown): Record<string, unknown> | null =>
      v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null

    const metadata = asRecord(data?.metadata)

    if (!metadata) return null

    const nsDoc = asRecord(metadata.nsDocument ?? metadata.NsDocument)
    const spreadMetaInfo = asRecord(nsDoc?.spreadMetaInfo ?? nsDoc?.SpreadMetaInfo)

    if (!spreadMetaInfo) return null

    return asRecord(spreadMetaInfo.editorSettings ?? spreadMetaInfo.EditorSettings)
  }, [data?.metadata])

  const checkpointDefaultFontName = useMemo(() => {
    const name = checkpointEditorSettings?.defaultFont ?? checkpointEditorSettings?.DefaultFont

    return typeof name === 'string' && name.trim() !== '' ? name.trim() : null
  }, [checkpointEditorSettings])

  const checkpointDefaultFontSize = useMemo(() => {
    const raw =
      checkpointEditorSettings?.defaultFontSize ?? checkpointEditorSettings?.DefaultFontSize

    if (raw == null) return null
    const n = Number(raw)

    return Number.isFinite(n) && n > 0 ? n : null
  }, [checkpointEditorSettings])

  const elementObjectIds = useMemo(() => {
    const refs = data?.elementReferences ?? data?.elements?.map((e) => e.reference) ?? []

    return refs.map((r) => r.objectId).filter((id): id is string => id.length > 0)
  }, [data?.elementReferences, data?.elements])

  // Change queue: batches element mutations with debouncing (fire-and-forget).
  // All payloads conform to the StowedElement contract.
  const {
    enqueueAdd,
    enqueueUpdate,
    enqueueDelete,
    flush: flushQueue,
  } = useChangeQueue({
    spreadId,
    layer: 'userLayer',
    debounceMs: 300,
    maxWaitMs: 2000,
  })

  const {
    enqueueGroup,
    enqueueUngroup,
    enqueueRestructure,
    enqueueReposition,
    flush: flushGroupActionQueue,
    isBusy: isGroupActionQueueBusy,
  } = useGroupActionQueue({
    spreadId,
    layer: 'userLayer',
    debounceMs: 0,
    maxWaitMs: 2000,
  })

  const flushQueueRef = React.useRef(flushQueue)
  const flushGroupActionQueueRef = React.useRef(flushGroupActionQueue)

  React.useEffect(() => {
    flushQueueRef.current = flushQueue
    flushGroupActionQueueRef.current = flushGroupActionQueue
  }, [flushQueue, flushGroupActionQueue])

  const { markEdited, release } = useElementsLock(spreadId, 'userLayer', elementObjectIds, {
    enabled: spreadId !== '',
    unlockIdleMs: 2000,
    beforeUnlock: async () => {
      await flushQueue()
      await flushGroupActionQueue()
    },
  })

  /** Flush pending mutations and unlock before leaving so the next spread checkpoint refetch sees saved state. */
  const handleSpreadIndexChange = useCallback(
    async (nextIndex: number) => {
      centerCanvasRef.current?.cancelPenToolDrawing()
      finalizeActiveShapeTextEdit(shapeManagerRef.current)
      // Frame ids do not survive the spread, and neither should an answer about one of its seams —
      // nor a pre-projection baseline for one of its frames.
      chainSeamMemoRef.current.clear()
      chainProjectionBaselineRef.current.clear()

      const exitingSpreadId = spreadId

      try {
        await flushQueue()
        await flushGroupActionQueue()
        await release()
        if (exitingSpreadId) {
          await flushSpreadBackgroundWritesRef.current?.(exitingSpreadId)
          await checkpointsService.exitCheckpoint(exitingSpreadId)
          // Refetch after exit so cache is fresh if the user navigates back.
          // Without this, the canvas loads from a stale snapshot that predates
          // any shapes committed just before navigation (e.g. pen-tool finish).
          await queryClient.refetchQueries({
            queryKey: [...queryKeys.spreadCheckpoint.all, 'byId', exitingSpreadId],
          })
        }
      } catch (err) {
        logger.warn('Pre-navigation flush/exit failed:', err)
      }

      setSelectedTextBoxPanelIds([])
      setHasTextSelection(false)
      setActiveTextBoxes([])
      setSpreadIndex(nextIndex)
    },
    [
      flushQueue,
      flushGroupActionQueue,
      release,
      spreadId,
      queryClient,
      setActiveTextBoxes,
      centerCanvasRef,
      chainProjectionBaselineRef,
      chainSeamMemoRef,
      setSelectedTextBoxPanelIds,
      setSpreadIndex,
      shapeManagerRef,
      setHasTextSelection,
    ]
  )

  // Returning to a spread must refetch checkpoint — default staleTime keeps prior snapshot for 30m.
  useEffect(() => {
    if (!spreadId) return

    void queryClient.invalidateQueries({
      queryKey: [...queryKeys.spreadCheckpoint.all, 'byId', spreadId],
    })
  }, [spreadId, queryClient])

  /** Best-effort exit-checkpoint on tab close / reload (httpClient is async; use keepalive fetch). */
  useEffect(() => {
    const sendExit = () => {
      // Commit in-progress inline edits (shape text) so the queue flush has them.
      finalizeActiveShapeTextEdit(shapeManagerRef.current)
      // keepalive: true — a plain in-flight fetch isn't guaranteed to finish once the browser
      // proceeds with unload/reload; the just-committed edits above need this to actually land
      // (WMPCM-7778: shape text committed right before reload was otherwise dropped in transit).
      void flushQueueRef.current({ keepalive: true })
      void flushGroupActionQueueRef.current({ keepalive: true })
      const sid = spreadIdRef.current

      if (!sid) return
      const jwt = getStoredJwtToken()
      const connId = getStoredConnectionId()

      if (!jwt) return
      const url = `${getApiBaseUrl().replace(/\/$/, '')}/checkpoints/${sid}/exit-checkpoint`

      void fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          Authorization: `Bearer ${jwt}`,
          ...(connId ? { 'X-Connection-Id': connId } : {}),
        },
      }).catch(() => {})
    }

    window.addEventListener('pagehide', sendExit)
    window.addEventListener('beforeunload', sendExit)
    return () => {
      window.removeEventListener('pagehide', sendExit)
      window.removeEventListener('beforeunload', sendExit)
    }
  }, [shapeManagerRef, spreadIdRef])

  /**
   * Deletes for what an undo/redo step removes, sent after any group request on its way.
   *
   * Group requests travel on their own queue, so a delete for what one creates could reach the
   * server first and leave the group behind. Undoing a template that is still saving does exactly
   * that: its groups are created one request at a time. Requests that were not sent yet are called
   * off by the step itself (see `pending-group-persist`), which leaves only a request already on the
   * wire to wait for. With the queue idle the deletes are enqueued at once.
   */
  const enqueueDeletesAfterGroupRequests = useCallback(
    (ids: readonly string[], options?: Parameters<typeof enqueueDelete>[1]) => {
      const enqueueDeletes = (toDelete: readonly string[]) => {
        for (const id of toDelete) {
          enqueueDelete(id, options)
        }
      }

      if (ids.length === 0 || !isGroupActionQueueBusy()) {
        enqueueDeletes(ids)
        return
      }

      void flushGroupActionQueue()
        // A failed flush must not cost the step its deletes.
        .catch((error: unknown) => logger.warn('Group queue flush failed before deletes', error))
        .then(() => {
          // The user may have redone the step while these waited, which puts the elements back (and
          // saves them): a delete now would remove them from the server but not the canvas.
          const live = new Set(shapeManagerRef.current?.getShapes().map((shape) => shape.id))

          enqueueDeletes(ids.filter((id) => !live.has(id)))
        })
    },
    [enqueueDelete, flushGroupActionQueue, isGroupActionQueueBusy, shapeManagerRef]
  )

  const applyHistoryPersistencePlan = useCallback(
    (
      plan: HistoryPersistencePlan,
      /**
       * The shape list as it stood before this history step mutated the manager. Only the chain
       * rider reads it, and only as a fallback — see there for why a rider this same step deleted
       * still has to be resolvable.
       */
      shapesBeforeStep?: readonly ShapeData[]
    ) => {
      const mgr = shapeManagerRef.current
      const historyMeta =
        plan.historyTxnId || plan.historyActionType || plan.historyDirection || plan.batchElementIds
          ? {
              historyTxnId: plan.historyTxnId,
              historyActionType: plan.historyActionType,
              historyDirection: plan.historyDirection,
              batchElementIds: plan.batchElementIds,
            }
          : undefined
      const isStructuralGroupTxn =
        !!plan.historyTxnId &&
        (plan.historyTxnId.startsWith('group:') || plan.historyTxnId.startsWith('ungroup:'))

      if (plan.shapeMarkEditedIds.length > 0) {
        void markEdited(plan.shapeMarkEditedIds)
      }

      if (plan.shapeTypeConversions && plan.shapeTypeConversions.length > 0) {
        for (const { deleteId, addShape } of plan.shapeTypeConversions) {
          enqueueDelete(deleteId, { meta: historyMeta })
          enqueueAdd(
            buildStowedShapeElement(
              shapeTextBakeRef.current,
              addShape,
              undefined,
              spreadProjection,
              fontLookupRef.current ?? undefined
            ),
            {
              isPenElement: addShape.type === 'path',
              meta: historyMeta,
            }
          )
        }
      }

      const textIdsCoalescedIntoMixedGroupBatch = new Set<string>()

      // Frontend rollout note: for group/ungroup transactions, structural persistence
      // is handled by the dedicated group action queue, so generic element upsert/delete
      // persistence must be bypassed to avoid duplicate writes.
      if (!isStructuralGroupTxn && mgr && plan.shapeUpserts.length > 0) {
        const shapes = mgr.getShapes()
        const mixedGeometric = mixedGeometricOpFromHistoryTxnId(plan.historyTxnId)
        const byId = new Map(shapes.map((s) => [s.id, s]))
        const batchGroupIds = new Set<string>()

        if (mixedGeometric) {
          for (const upsert of plan.shapeUpserts) {
            const s = upsert.shape ?? shapes.find((x) => x.id === upsert.shapeId)

            if (s && isGroupShape(s)) batchGroupIds.add(s.id)
          }
        }

        for (const upsert of plan.shapeUpserts) {
          const shape = upsert.shape ?? shapes.find((s) => s.id === upsert.shapeId)

          if (!shape) continue

          if (mixedGeometric && batchGroupIds.size > 0 && isGroupShape(shape)) {
            const groupPersistOp = groupGeometricPersistOpFromTransformSource(mixedGeometric)
            const stowedBatch = buildGroupTransformPersistBatch(
              shapes,
              shape,
              [],
              groupPersistOp,
              spreadProjection,
              fontLookupRef.current ?? undefined
            )

            for (const row of stowedBatch) {
              if (isStowedTextElement(row)) textIdsCoalescedIntoMixedGroupBatch.add(row.objectId)
            }

            if (mixedGeometric === 'rotate') {
              logGroupRotateDebug({
                label: 'batch',
                group: shape,
                descendants: shapes.filter((s) => s.parentGroupId === shape.id),
                stowedBatch,
              })
            }

            enqueueReposition(
              buildGroupRepositionPayload(
                shape,
                groupPersistOp,
                stowedBatch,
                plan.historyTxnId ? { historyTxnId: plan.historyTxnId } : undefined
              )
            )
            continue
          }

          if (mixedGeometric && batchGroupIds.size > 0) {
            const subtreePersistedWithGroupReposition =
              (mixedGeometric === 'rotate' ||
                mixedGeometric === 'move' ||
                mixedGeometric === 'resize') &&
              [...batchGroupIds].some((gid) => isDescendantOfGroup(shape, gid, byId))

            if (subtreePersistedWithGroupReposition) continue
          }

          if (isGroupShape(shape)) continue
          if (upsert.mode === 'add') {
            enqueueAdd(
              buildStowedShapeElement(
                shapeTextBakeRef.current,
                shape,
                undefined,
                spreadProjection,
                fontLookupRef.current ?? undefined
              ),
              {
                meta: historyMeta,
                isPenElement: shape.type === 'path',
              }
            )
          } else {
            enqueueUpdate(
              buildStowedShapeElement(
                shapeTextBakeRef.current,
                shape,
                buildStowedVisibleRectOptions(shape.rotation, upsert.operation),
                spreadProjection,
                fontLookupRef.current ?? undefined
              ),
              {
                operation: upsert.operation,
                meta: historyMeta,
              }
            )
          }
        }
      }

      // Deliberately ahead of the deletes below. `ChangeQueue` coalesces per objectId, and a
      // whole-chain delete rides its prune on a frame this same step is deleting (see
      // `repairChainAfterFrameDeleted`'s no-survivor branch). Enqueued after the delete, the update
      // is dropped outright by the pending-delete rule; enqueued before it, the queue's send-ahead
      // exception puts it on the wire first. Redoing a whole-chain delete silently re-orphaned the
      // chain until this moved (WMPCM-7036 AC4).
      if (plan.linkedTextChainsUpsert) {
        const { chains, riderTextBoxId } = plan.linkedTextChainsUpsert

        // Advance the live mirror alongside the state write, same as `repairChainAfterFrameDeleted`
        // does — otherwise the ref stays a render behind for the rest of this history step.
        textChainsRef.current = chains
        setTextChains(chains)

        // A rider is always a ShapeManager shape now (text-unification) — resolve it by exact id
        // only, from the live list first and then from the pre-step list. Still deliberately no
        // "any frame with a uid" fallback: persisting the chain onto an arbitrary *unrelated* frame
        // is worse than persisting nothing. The pre-step lookup is not that — it is the same
        // element, read one step earlier, and it is required because a total-chain delete rides on
        // the frame being deleted, which `applyHistoryAction` has already pulled out of the manager
        // by the time this runs.
        const riderShape =
          mgr?.getShapes().find((s) => s.id === riderTextBoxId) ??
          shapesBeforeStep?.find((s) => s.id === riderTextBoxId)

        if (riderShape) {
          // Union of both lists, live shapes taking precedence — same "live first" preference as
          // `riderShape` above — so a member's uid resolves to its objectId whichever list it
          // currently lives in. See `toWireTextChains`.
          const shapesForWire = [...(shapesBeforeStep ?? []), ...(mgr?.getShapes() ?? [])]

          enqueueUpdate(
            buildStowedShapeElement(
              shapeTextBakeRef.current,
              riderShape,
              undefined,
              spreadProjection,
              fontLookupRef.current ?? undefined
            ),
            {
              operation: 'update',
              linkedTextElements: { textChain: toWireTextChains(chains, shapesForWire) },
              meta: historyMeta,
            }
          )
        }
      }

      if (!isStructuralGroupTxn) {
        const deleteIds = plan.shapeDeleteIds.filter(
          (shapeId) =>
            !mgr?.getShapes().some((shape) => shape.id === shapeId && isGroupShape(shape))
        )

        enqueueDeletesAfterGroupRequests(deleteIds, { meta: historyMeta })
      }

      if (plan.shapeResequenceIds.length > 0 && mgr) {
        const allShapes = mgr.getShapes()
        const stubs = plan.shapeResequenceIds
          .map((id) => allShapes.find((s) => s.id === id))
          .filter((s): s is ShapeData => s != null)
          .map((s) =>
            buildStowedShapeElement(
              shapeTextBakeRef.current,
              s,
              undefined,
              spreadProjection,
              fontLookupRef.current ?? undefined
            )
          )

        if (stubs.length > 0) {
          void elementsService.resequenceElements(spreadId, 'userLayer', stubs)
        }
      }

      if (plan.unifiedLayerOrder) {
        unifiedLayerIdsRef.current = plan.unifiedLayerOrder
        setUnifiedLayerIds(plan.unifiedLayerOrder)
      }

      if (plan.spreadBackgroundsRestore) {
        const { spreadId: targetSpreadId, slots } = plan.spreadBackgroundsRestore

        const direction = plan.historyDirection

        if (restoreSpreadBackgroundsRef.current) {
          // A replay that doesn't take effect is settled so undo/redo stay in step with what's
          // saved: retried later or dropped, depending on the failure.
          restoreSpreadBackgroundsRef.current(targetSpreadId, slots, (failure) =>
            settleFailedBackgroundRestore(direction, slots, failure, {
              revertHistoryStepIfMatches,
              dropReplayedStepIfMatches,
            })
          )
        } else {
          logger.warn('Spread background history step skipped: no background bridge mounted')
        }
      }

      if (plan.syncActiveFromShapeSelection) {
        syncActiveFromShapeSelection()
      }
    },
    [
      enqueueAdd,
      enqueueDelete,
      enqueueReposition,
      enqueueUpdate,
      enqueueDeletesAfterGroupRequests,
      isGroupShape,
      markEdited,
      spreadId,
      spreadProjection,
      syncActiveFromShapeSelection,
      fontLookupRef,
      setTextChains,
      setUnifiedLayerIds,
      shapeManagerRef,
      shapeTextBakeRef,
      textChainsRef,
      unifiedLayerIdsRef,
      revertHistoryStepIfMatches,
      dropReplayedStepIfMatches,
    ]
  )

  return {
    canUndo,
    canRedo,
    pushAction,
    undo,
    redo,
    getUndoStackSize,
    popIfMatches,
    enqueueAdd,
    enqueueUpdate,
    enqueueDelete,
    flushQueue,
    enqueueGroup,
    enqueueUngroup,
    enqueueRestructure,
    enqueueReposition,
    flushGroupActionQueue,
    enqueueDeletesAfterGroupRequests,
    markEdited,
    release,
    pendingUngroupChildrenByTxnRef,
    isGroupShape,
    isGroupTxn,
    isUngroupTxn,
    isParentGroupOnlyUndo,
    handleTransparencyCommitApplied,
    applyActiveShapeSelection,
    syncActiveFromShapeSelection,
    checkpointEditorSettings,
    checkpointDefaultFontName,
    checkpointDefaultFontSize,
    elementObjectIds,
    flushQueueRef,
    flushGroupActionQueueRef,
    handleSpreadIndexChange,
    applyHistoryPersistencePlan,
    restoreSpreadBackgroundsRef,
    flushSpreadBackgroundWritesRef,
    recordSpreadBackgroundsChange,
  }
}
